/*
  Copyright 2018 Experian. All Rights Reserved
*/

import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isObject } from 'lodash';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { BaseSearchParams } from './model/base-search-params.interface';
import { ErrorResponse } from './model/error-response.interface';
import { HeadersRequest } from './model/header-request.inteface';

import { environment } from '../../../../environments/environment';

@Injectable({providedIn: 'root'})
export class ApiService {
  private readonly contentTypeHeaderName: string = 'Content-Type';
  private readonly contentTypeHeaderJsonValue: string = 'application/json';

  allowedUrls: string[] = [
    environment.iam.user.authIdentityStrong,
    environment.iam.user.changeAccount,
    environment.iam.user.leverageIdentifyStrong,
    environment.userAccounts.me
  ];

  constructor(private httpClient: HttpClient) {
  }

  needsToken(url: string): boolean {
    return !this.allowedUrls.some(x => url.indexOf(x) >= 0);
  }

  get<T, S extends BaseSearchParams = {}>(url: string, queryParams?: S, headers?: Array<HeadersRequest>): any {
    return this.httpClient.get<T>(url,
      {
        headers: this.getHeadersRequestOptions(headers, url),
        params: queryParams as any,
        observe: 'response'
      }).pipe(catchError((errorHttpResponse) => this.handleErrors(errorHttpResponse)));
  }

  post<T, S extends BaseSearchParams = {}>(url: string, data?: any, headers?: Array<HeadersRequest>, queryParams?: S):
    Observable<HttpResponse<T>> {
    return this.httpClient.post<T>(url,
      data,
      {
        headers: this.getHeadersRequestOptions(headers, url),
        params: queryParams as any,
        observe: 'response'
      }).pipe(timeout(60000));
  }

  put<T, S extends BaseSearchParams = {}>(url: string, data: any, queryParams?: S, headers?: Array<HeadersRequest>): any {
    return this.httpClient.put<T>(url,
      data,
      {
        headers: this.getHeadersRequestOptions(headers, url),
        params: queryParams as any,
        observe: 'response'
      }).pipe(catchError((errorHttpResponse) => this.handleErrors(errorHttpResponse)));
  }

  patch<T>(url: string, data: any): any {
    return this.httpClient.patch<T>(url,
      data,
      {
        headers: this.getHeadersRequestOptions(),
        observe: 'response'
      }).pipe(catchError((errorHttpResponse) => this.handleErrors(errorHttpResponse)));
  }

  delete<T>(url: string, headers?: Array<HeadersRequest>): any {
    return this.httpClient.delete<T>(url, {
      headers: this.getHeadersRequestOptions(headers, url),
      observe: 'response'
    }).pipe(catchError((errorHttpResponse) => this.handleErrors(errorHttpResponse)));
  }

  private getHeadersRequestOptions(headers?: Array<HeadersRequest>, url?: string): HttpHeaders {
    let httpHeaders: HttpHeaders = new HttpHeaders({
      [this.contentTypeHeaderName]: this.contentTypeHeaderJsonValue
    });

    if (headers) {
      headers.forEach(({param, value}) => httpHeaders = httpHeaders.set(param, value));
    }

    if (!httpHeaders.has('Authorization') && (this.needsToken(url))) {
      const token = sessionStorage.getItem('accessToken');
      if (token) {
        httpHeaders = httpHeaders.set('Authorization', `Bearer ${token}`);
      }
    }

    return httpHeaders;
  }

  private handleErrors(errorHttpResponse: HttpErrorResponse): any {
    const apiErrorResponse: ErrorResponse = {
      status: 500,
      error: [{code: '500', message: 'errors.internal.server.error'}]
    };

    if (this.hasValidJsonResponse(errorHttpResponse)) {
      apiErrorResponse.error = errorHttpResponse.error;
      apiErrorResponse.status = errorHttpResponse.status;
    } else {
      // TODO: this.notificationService.showError(head(apiErrorResponse.errors));
    }

    return throwError(() => apiErrorResponse);
  }

  private hasValidJsonResponse(errorHttpResponse: HttpErrorResponse): boolean {
    return errorHttpResponse.headers
      && errorHttpResponse.error
      && errorHttpResponse.headers.get(this.contentTypeHeaderName).indexOf(this.contentTypeHeaderJsonValue) !== -1
      && this.isErrorResponse(errorHttpResponse.error);
  }

  private hasContentTypeInHeader(errorHttpResponse: any): boolean {
    return errorHttpResponse.headers.get(this.contentTypeHeaderName).indexOf(this.contentTypeHeaderJsonValue) !== -1;
  }

  private isErrorResponse(errorResponse: ErrorResponse): boolean {
    return isObject(errorResponse);
    // && 'errors' in errorResponse;
  }
}
